// Durability for per-conversation composer drafts.
//
// The rules about what is worth keeping live in pages/Chat/composerDrafts.ts;
// this module only answers "where does it go so it is still there tomorrow".
//
// Why localStorage rather than the IndexedDB cache next door: the read is
// synchronous. Chat hydrates the whole map once at mount and then restores
// drafts straight out of memory on every conversation switch, so there is no
// window in which a late-resolving promise overwrites what someone is typing.
// It also keeps drafts outside `enforceSizeCap`, which would otherwise be free
// to evict an unsent message to make room for an HTTP response.
//
// Drafts are message content, so they are namespaced by user id and wiped on
// logout — see clearDraftsForNamespace / clearForeignDraftNamespaces, called
// from AuthContext beside the equivalent cache wipes.

import { getCacheNamespace } from '../cache/namespace';
import {
  MAX_DRAFT_CONVERSATIONS,
  createDraftStore,
  type DraftStore,
} from '../../pages/Chat/composerDrafts';

export const DRAFTS_KEY_PREFIX = 'teamlio:chat-drafts:';

export const draftsStorageKey = (ns: string): string => `${DRAFTS_KEY_PREFIX}${ns}`;

/** Newest-last, so trimming from the front drops the least recently written. */
const trimToCap = (entries: [string, string][]): [string, string][] =>
  entries.length > MAX_DRAFT_CONVERSATIONS
    ? entries.slice(entries.length - MAX_DRAFT_CONVERSATIONS)
    : entries;

/**
 * Every draft this user has pending. A missing, unreadable or corrupt blob all
 * mean the same thing to the caller — no drafts — because the alternative is
 * throwing on the path that paints the composer.
 */
export function loadDrafts(ns: string = getCacheNamespace()): DraftStore {
  const store = createDraftStore();
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(draftsStorageKey(ns));
  } catch {
    return store;
  }
  if (!raw) return store;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return store;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return store;

  // Anything that isn't text is skipped rather than coerced: a draft is what
  // the user typed, and `String(42)` is not that.
  const entries = Object.entries(parsed as Record<string, unknown>)
    .filter((e): e is [string, string] => typeof e[1] === 'string' && e[1].length > 0);

  for (const [convId, text] of trimToCap(entries)) store.set(convId, text);
  return store;
}

/**
 * Persist the whole map. An empty map removes the key outright rather than
 * leaving `{}` behind, so a user who sends everything leaves nothing on disk.
 *
 * A quota rejection sheds the oldest half and tries once more; if that still
 * fails the drafts simply stay in memory for this session. Losing persistence
 * is worth far less than an exception on a keystroke.
 */
export function saveDrafts(store: DraftStore, ns: string = getCacheNamespace()): void {
  const key = draftsStorageKey(ns);

  if (store.size === 0) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable — nothing to clean up */
    }
    return;
  }

  const entries = trimToCap([...store.entries()]);
  const write = (payload: [string, string][]) => {
    localStorage.setItem(key, JSON.stringify(Object.fromEntries(payload)));
  };

  try {
    write(entries);
  } catch (err) {
    if (!isQuotaError(err)) return;
    try {
      write(entries.slice(Math.floor(entries.length / 2)));
    } catch {
      /* still doesn't fit — memory-only for this session */
    }
  }
}

function isQuotaError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED';
}

/** Forget one user's drafts — called on logout, beside the cache wipe. */
export function clearDraftsForNamespace(ns: string): void {
  try {
    localStorage.removeItem(draftsStorageKey(ns));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Forget every other user's drafts — called when a session is adopted, so a
 * new login on a shared machine cannot leave the previous user's unsent
 * messages sitting on disk.
 */
export function clearForeignDraftNamespaces(keepNs: string): void {
  try {
    const keep = draftsStorageKey(keepNs);
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DRAFTS_KEY_PREFIX) && key !== keep) doomed.push(key);
    }
    doomed.forEach(key => localStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
}
